/**
 * Global UI State Store
 * Centralizes modal open/close states and mode toggles so any component
 * can read/write them without prop drilling through MainApp.
 * Uses useSyncExternalStore — no Zustand needed.
 */
import { useSyncExternalStore } from 'react';

export type EffortLevel = 'low' | 'medium' | 'high';

/**
 * Centralized localStorage keys to improve type safety and prevent typos.
 * All localStorage key literals should be defined here.
 */
export const enum LocalStorageKey {
  FOCUS_MODE = 'msapling_focus_mode',
  DARK_MODE = 'msapling_dark_mode',
  ONBOARDING_COMPLETE = 'msapling_onboarding_complete',
  SESSION_ID = 'msapling_session_id',
  APPEARANCE_THEME = 'msapling_appearance_theme',
  APPEARANCE_COMPACT = 'msapling_appearance_compact',
  CHAT_SCROLL_PREFIX = 'msapling_chat_scroll_v1:',
  COOKIE_CONSENT = 'msapling_cookie_consent',
  SHORTCUTS = 'msapling_shortcuts',
  SIDEBAR_DM_READ_POINTERS = 'msapling_sidebar_dm_read_pointers_v1',
  SIDEBAR_PINNED_ACTIONS = 'msapling_sidebar_pinned_actions_v1',
  PANEL_COLOR_PREFIX = 'msapling_panel_color_',
  AUTH_STATE = 'msapling_auth_state_v1',
  NOTIFICATIONS_ENABLED = 'msapling_notifications_enabled',
  TELEMETRY_OPT_OUT = 'msapling_telemetry_opt_out',
  GUEST_EXIT_INTENT_DISMISSED_AT = 'msapling_guest_exit_intent_dismissed_at',
  GUEST_CLAIM_CLAIMED = 'msapling_guest_claim_claimed',
}

const ONBOARDING_KEY = LocalStorageKey.ONBOARDING_COMPLETE;

export interface GlobalUIState {
  // Mode toggles
  planMode: boolean;
  fastMode: boolean;
  vimMode: boolean;
  workerMode: boolean;
  focusMode: boolean;
  effortLevel: EffortLevel;
  // Modal visibility
  isSettingsOpen: boolean;
  isToolsOpen: boolean;
  showCommandPalette: boolean;
  doctorModalOpen: boolean;
  diffModalOpen: boolean;
  initModalOpen: boolean;
  commandCenterOpen: boolean;
  // Connectivity
  isOnline: boolean;
  isBackendReachable: boolean;
  selectedModel: string;
  // Layout
  sidebarOpen: boolean;
  darkMode: boolean;
  // Onboarding
  onboardingComplete: boolean;
}

const INITIAL: GlobalUIState = {
  planMode: false,
  fastMode: false,
  vimMode: false,
  workerMode: false,
  focusMode: typeof window !== 'undefined' && localStorage.getItem(LocalStorageKey.FOCUS_MODE) === '1',
  effortLevel: 'medium',
  isSettingsOpen: false,
  isToolsOpen: false,
  showCommandPalette: false,
  doctorModalOpen: false,
  diffModalOpen: false,
  initModalOpen: false,
  commandCenterOpen: false,
  isOnline: true,
  isBackendReachable: true,
  selectedModel: '',
  sidebarOpen: true,
  darkMode: typeof window !== 'undefined' && localStorage.getItem(LocalStorageKey.DARK_MODE) === '1',
  onboardingComplete: typeof window !== 'undefined' && localStorage.getItem(ONBOARDING_KEY) === 'true',
};

// Keep the SSR/hydration snapshot independent of browser storage. The client
// state can pick up persisted values through INITIAL, then React will reconcile
// to that state after hydration without producing a server/client mismatch.
const SERVER_SNAPSHOT: GlobalUIState = Object.freeze({
  ...INITIAL,
  focusMode: false,
  darkMode: false,
  onboardingComplete: false,
});

const listeners = new Set<() => void>();
let state: GlobalUIState = { ...INITIAL };

const emit = (): void => listeners.forEach((l) => l());

const subscribe = (l: () => void): (() => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

const getSnapshot = (): GlobalUIState => state;

export const globalUIStore = {
  subscribe,
  getSnapshot,
  update: (patch: Partial<GlobalUIState>): void => {
    state = { ...state, ...patch };
    emit();
  },
  toggle: (key: keyof Pick<GlobalUIState, 'planMode' | 'fastMode' | 'vimMode' | 'isSettingsOpen' | 'isToolsOpen' | 'showCommandPalette' | 'doctorModalOpen' | 'diffModalOpen' | 'initModalOpen'>): void => {
    state = { ...state, [key]: !state[key] };
    emit();
  },
  reset: (): void => {
    state = { ...INITIAL };
    emit();
  },
};

export const useGlobalUI = (): GlobalUIState =>
  useSyncExternalStore(subscribe, getSnapshot, () => SERVER_SNAPSHOT);

export const setCommandCenterOpen = (open: boolean): void => globalUIStore.update({ commandCenterOpen: open });
export const setWorkerModeGlobal = (enabled: boolean): void => globalUIStore.update({ workerMode: enabled });
export const setIsOnline = (on: boolean): void => globalUIStore.update({ isOnline: on });
export const setIsBackendReachable = (on: boolean): void => globalUIStore.update({ isBackendReachable: on });
export const setSelectedModel = (model: string): void => globalUIStore.update({ selectedModel: model });
export const completeOnboarding = (): void => {
  if (typeof window !== 'undefined') localStorage.setItem(LocalStorageKey.ONBOARDING_COMPLETE, 'true');
  globalUIStore.update({ onboardingComplete: true });
};
